import { call, put, select } from 'redux-saga/effects';
import { callWithAuth } from '../../sagaApiHelper';
import { getTemplatesApi } from '../../../api/apiLists/getTemplatesApi';
import { normalizeTemplates as normalizeTemplatesUtil } from '../../../utils/normalizeTemplates';
import { showAlert } from '../../alertSlice';
import { setTemplates as setWhatsappTemplates } from '../../whatsappSlice';
import { fetchTemplatesStart, fetchTemplatesSuccess, appendTemplates, fetchTemplatesError, CACHE_TTL_MS, fetchTemplateLibraryStart, fetchTemplateLibrarySuccess, fetchTemplateLibraryError } from '../../slices/templatesSlice';
import { getOrFetchWabaId } from './utils';
import { getTemplateLibraryApi } from '../../../api/apiLists/getTemplateLibraryApi';

const isTemplateCacheValid = (cachedAt) => {
  if (!cachedAt) return false;
  return (Date.now() - cachedAt) < CACHE_TTL_MS;
};

function* fetchTemplatesWithFilters(wabaId, filters = [], limit = 100) {
  const res = yield call(callWithAuth, getTemplatesApi, { 
    operation: 'GETTEMPLATES', 
    payload: {
      wabaId,
      filters: filters.length > 0 ? filters : undefined,
      limit
    }
  });

  if (res?.status === true) {
    const list = Array.isArray(res.data?.result?.data) ? res.data.result.data : [];
    const localClones = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('template_library_cloned_')) {
        localClones[key] = localStorage.getItem(key);
      }
    }
    return {
      templates: normalizeTemplatesUtil(list, localClones),
      lastEvaluatedKey: res.data?.result?.lastEvaluatedKey || null
    };
  }

  return { templates: [], lastEvaluatedKey: null };
}

export function* handleLoadTemplates(action) {
  const { 
    category = 'utility', 
    searchQuery = '', 
    limit = 50, 
    lastEvaluatedKey = null,
    isLoadMore = false,
    isRefresh = false,
    loadAll = false,
    loadAllWithSearch = false
  } = action.payload || {};

  const wabaId = yield* getOrFetchWabaId();
  if (!wabaId) {
    yield put(showAlert({ message: 'WhatsApp Business Account not found for your company. Please Connect with Meta first.', type: 'error' }));
    return;
  }

  if (loadAllWithSearch) {
    const filters = searchQuery?.trim() ? [{ key: 'name', value: searchQuery.trim(), op: 'CONTAINS' }] : [];
    const { templates } = yield* fetchTemplatesWithFilters(wabaId, filters, limit);
    yield put(setWhatsappTemplates(templates));
    return;
  }

  if (loadAll) {
    const { templates } = yield* fetchTemplatesWithFilters(wabaId, [], limit);
    yield put(setWhatsappTemplates(templates));
    return;
  }

  const categoryMap = { utility: 'UTILITY', marketing: 'MARKETING', authentication: 'AUTHENTICATION' };
  const categoryValue = categoryMap[String(category).toLowerCase()] || 'UTILITY';

  const canUseCache = !searchQuery && !lastEvaluatedKey && !isLoadMore && !isRefresh;
  const categoryDataState = yield select(state => state.templates?.categoryData?.[category]);
  const hasValidCache = canUseCache && categoryDataState?.templates?.length > 0 && isTemplateCacheValid(categoryDataState?.cachedAt);

  if (hasValidCache) {
    yield put(fetchTemplatesSuccess({
      category,
      templates: categoryDataState.templates,
      lastEvaluatedKey: categoryDataState.lastEvaluatedKey,
      hasMore: categoryDataState.hasMore
    }));
    return;
  }

  try {
    yield put(fetchTemplatesStart(category));

    const filters = [{ key: 'category', value: categoryValue }];
    if (searchQuery?.trim()) {
      filters.push({ key: 'name', value: searchQuery.trim(), op: 'CONTAINS' });
    }

    const payload = { wabaId, filters, limit };
    if (lastEvaluatedKey) payload.lastEvaluatedKey = lastEvaluatedKey;

    const res = yield call(callWithAuth, getTemplatesApi, { operation: 'GETTEMPLATES', payload });

    if (res?.status === true) {
      const list = Array.isArray(res.data?.result?.data) ? res.data.result.data : [];
      const newLastEvaluatedKey = res.data?.result?.lastEvaluatedKey || null;
      const hasMore = !!newLastEvaluatedKey;
      const localClones = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('template_library_cloned_')) {
          localClones[key] = localStorage.getItem(key);
        }
      }
      const normalized = normalizeTemplatesUtil(list, localClones);

      if (isLoadMore) {
        yield put(appendTemplates({ category, templates: normalized, lastEvaluatedKey: newLastEvaluatedKey, hasMore }));
      } else {
        yield put(fetchTemplatesSuccess({ category, templates: normalized, lastEvaluatedKey: newLastEvaluatedKey, hasMore }));
      }
    } else {
      const errorMsg = res?.errMsg || 'Failed to load templates';
      yield put(fetchTemplatesError({ category, error: errorMsg }));
      yield put(showAlert({ message: errorMsg, type: 'error' }));
    }
  } catch (error) {
    yield put(fetchTemplatesError({ category, error: 'Error occurred while loading templates' }));
    yield put(showAlert({ message: 'Error occurred while loading templates', type: 'error' }));
  }
}

export function* handleLoadTemplateLibrary(action) {
  const { isRefresh = false } = action.payload || {};

  const wabaId = yield* getOrFetchWabaId();
  if (!wabaId) {
    yield put(showAlert({ message: 'WhatsApp Business Account not found for your company. Please Connect with Meta first.', type: 'error' }));
    return;
  }

  const canUseCache = !isRefresh;
  const libraryState = yield select(state => state.templates?.categoryData?.library);
  const hasValidCache = canUseCache && libraryState?.templates?.length > 0 && isTemplateCacheValid(libraryState?.cachedAt);

  if (hasValidCache) {
    yield put(fetchTemplateLibrarySuccess({ templates: libraryState.templates }));
    return;
  }

  try {
    yield put(fetchTemplateLibraryStart());

    const res = yield call(callWithAuth, getTemplateLibraryApi, { 
      operation: 'GET_TEMPLATE_LIBRARY', 
      payload: { wabaId, language: ['en', 'hi'] } 
    });

    if (res?.status === true) {
      let list = [];
      if (Array.isArray(res.data?.result?.data)) {
        list = res.data.result.data;
      } else if (Array.isArray(res.data?.data)) {
        list = res.data.data;
      } else if (Array.isArray(res.data)) {
        list = res.data;
      }
      yield put(fetchTemplateLibrarySuccess({ templates: list }));
    } else {
      const errorMsg = res?.errMsg || 'Failed to load template library';
      yield put(fetchTemplateLibraryError({ error: errorMsg }));
      yield put(showAlert({ message: errorMsg, type: 'error' }));
    }
  } catch (error) {
    yield put(fetchTemplateLibraryError({ error: 'Error occurred while loading template library' }));
    yield put(showAlert({ message: 'Error occurred while loading template library', type: 'error' }));
  }
}
